import Axios from "axios"
import { Franchise } from "~/models/Franchise"
import { REQUIRED_OP } from "~/data/RequiredOPDatas"
import { ID } from "~/models/Identification"
import { MailData } from "~/models/Mail"
import { EstimateParams, EstimateResponse } from "~/models/Estimate"
import { TokenResponse } from "~/models/TokenResponse"
import { Customer } from "~/models/Customer"
import { Product, ProductCategory } from "~/models/Product"
import { NewTenantContractApplyContent } from "~/models/ContractItem"
import { FcCarSales } from "~/utils/FcCarSales"
import { env } from "~/utils/env"
import { ItemContractQuantityReq, ItemContractQuantityRes, ItemData, PriorityData, SettingData } from "~/models/Csv"
import csv from "csvtojson"
import { CacheAxiosResponse, setupCache } from "axios-cache-interceptor"

const instance = Axios.create()
const axios = setupCache(instance)

export enum EstimateType {
  SAVE = "1",
  COMMIT = "2",
}

export namespace Api {
  /**
   * 団体取得
   */
  export const getFcData = (): Promise<Franchise[]> => {
    return new Promise((resolve, reject) => {
      axios.get(env("NEXT_PUBLIC_FC_DATA_API")).then((res: any) => {
        const data = res.data
        const searchResultList = data.searchResultList || []
        const result = {}
        searchResultList.map((item: any, index) => {
          if (result[FcCarSales(item.fcGroupId)]) {
            result[FcCarSales(item.fcGroupId)].category.push(item.fcGroupBusinessDiv)
          } else {
            result[FcCarSales(item.fcGroupId)] = {
              id: FcCarSales(item.fcGroupId),
              name: item.fcGroupName,
              category: [item.fcGroupBusinessDiv],
              sort: item.positiveInteger
            }
          }
        })
        resolve(Object.values(result))
      }).catch(reject)
    })
  }
  /**
   * オプション取得
   */
  export const getOptions = (id: ID): Promise<ID[]> => {
    return new Promise((resolve, reject) => {
      const params = {
        c: JSON.stringify({ g1: [`itempackid:eq:${id}`] }),
        p: 1,
        s: 100
      }
      axios.get(env("NEXT_PUBLIC_INCLUSION_OPTIONS_API"), { params }).then((res: any) => {
        resolve((res.data.searchResultList || []).map((item: any) => item.itemCatalogId))
      }).catch(reject)
    })
  }

  /**
   * 必須オプション取得
   */
  export const getRequiredOptions = (id: ID): ID[] => {
    return REQUIRED_OP.filter(item => item.id === id).map(item => item.id)
  }

  /**
   * パックオプション取得
   */
  export const getPackOption = (): Promise<{ [P in ProductCategory]: Product[] }> => {
    return new Promise((resolve, reject) => {
      const results: { [P in ProductCategory]: Product[] } = {
        basicPack: [],
        rolePack: [],
        basicFnc: [],
        addFnc: [],
        pygFnc: [],
        dataFnc: [],
        mainFnc: [],
        mainPygFnc: [],
        othopt: [],
        othFnc: [],
        sptFnc: [],
        maService: [],
        insSup: [],
        sptMonthly: [],
      }
      // @see https://dev-gitlab.broadleaf.jp/blcloud-webfrontend-library/bl-datamodel/-/blob/develop/src/enum/contract/item-category.ts
      const categoryIdName = {
        "0": "mainFnc",
        "1": "mainPygFnc",
        "2": "addFnc",
        "3": "pygFnc",
        "9": "othopt",
        "10": "basicPack",
        "11": "rolePack",
        "12": "dataFnc",
        "13": "othFnc",
        "14": "sptFnc",
        "15": "sptMonthly",
        "16": "sptFnc",
      }
      // return resolve(JSON.parse(localStorage.getItem("data")))
      axios.get(env("NEXT_PUBLIC_ITEM_CATALOG_API")).then((res: any) => {
        const data = res.data
        Promise.all(data.searchResultList.map((item: any) => getOptions(item.itemCatalogId))).then(optionDatas => {
          data.searchResultList.forEach((item: any, index) => {
            const costData = item.itemCatalogAmountInfoList.length > 0 ? item.itemCatalogAmountInfoList[0].itemCatalogUnitPriceTaxExc : null
            const categoryName: ProductCategory = categoryIdName[item.itemCategory]
            if (categoryName) {
              const amounts = Object.fromEntries(item.itemCatalogAmountInfoList.map(amount => [amount.typeContractDiv, amount.itemCatalogUnitPriceTaxExc]))
              results[categoryName].push(
                {
                  id: item.itemCatalogId,
                  providerTenantId: item.providerTenantId,
                  name: item.catalogName,
                  categoryId: item.itemCategory,
                  category: categoryName,
                  itemKind: item.itemKind,
                  price: {
                    initialCost: costData,
                    standardCost: costData,
                    amounts
                  },
                  explain: item.catalogDescription,
                  options: optionDatas[0],
                  requiredOP: item.itemCatalogId ? getRequiredOptions(item.itemCatalogId) : [],
                  multiDiscount: item.itemCatalogAmountInfoList.length > 0 ? item.itemCatalogAmountInfoList.length > 1 ? 1 : 2 : 0,
                  payBS: false,
                  contractServiceClassIdList: item.contractServiceClassIdList
                } as Product
              )
            }
          })
          resolve(results)
        })
      }).catch(reject)
    })
  }

  /**
   * アクセストークン取得
   */
  export function getAccessToken(token: string): Promise<TokenResponse> {
    return new Promise((resolve, reject) => {
      axios.post(env("NEXT_PUBLIC_GET_ACCESS_TOKEN_API"), {
        imageAuthResult: token
      }).then((response: CacheAxiosResponse) => {
        resolve(response.data)
      }).catch(reject)
    })
  }

  /**
   * メール送信
   */
  export function sendMail(data: MailData) {
    return new Promise((resolve, reject) => {
      const { estimateId, accessTokenId, mailType, email, name, picName, tel } = data
      axios.post(env("NEXT_PUBLIC_MAIL_SEND_API"), {
        itemintroductionestimateid: estimateId,
        accesstokenid: accessTokenId,
        itemIntroductionMailDiv: mailType,
        emailAddress: email,
        customerName1: name,
        picName,
        showPageDiv: "1",
        companyPhoneNumber: tel,
      }).then((response: CacheAxiosResponse) => {
        resolve(Boolean(String(response.status).match(/^2\d\d/)))
      }).catch(error => {
        reject({ api: "sendMail", response: error.response })
      })
    })
  }

  export function getItemEstimateAPI(estimateId: string, accessTokenId: string): Promise<EstimateResponse> {
    return new Promise((resolve, reject) => {
      axios.get(`${env("NEXT_PUBLIC_GET_ITEM_ESTIMATE_API")}${estimateId}_${accessTokenId}`).then((response: CacheAxiosResponse) => {
        if (Boolean(String(response.status).match(/^2\d\d/))) {
          const data: EstimateResponse = response.data
          const expDateTime = Date.parse(data.expireDateTime)
          const now = new Date().getTime()
          if ((now > expDateTime)) {
            resolve(null)
          } else {
            resolve({ ...data, inputParameter: JSON.parse(data.inputParameter) })
          }
        }
      }).catch(reject)
    })
  }

  /**
   このAPIはお客様情報入力後のメール送信前と、合計見積確認画面でメールを送信するAPIの前に実行されます。
   */
  export function putItemEstimateAPI(estimateId: string, accessTokenId: string, inputParameter: Customer, status: `${EstimateType}` = EstimateType.SAVE): Promise<EstimateResponse> {
    return new Promise((resolve, reject) => {
      const params: EstimateParams = {
        itemIntroductionEstimateId: estimateId,
        accessTokenId,
        inputParameter: JSON.stringify(inputParameter),
        contractApplyStatusDiv: status
      }
      axios.put(`${env("NEXT_PUBLIC_PUT_ITEM_ESTIMATE_API")}${estimateId}_${accessTokenId}`, params).then((response: CacheAxiosResponse) => {
        const data: EstimateResponse = response.data
        resolve({ ...data, inputParameter: JSON.parse(data.inputParameter) })
      }).catch(error => {
        reject({ api: "putItemEstimateAPI", response: error.response })
      })
    })
  }

  /**
   * 契約申し込み
   * @param itemIntroductionEstimateId 商品紹介見積ID
   * @param newTenantContractApplyContentList 新規テナント契約申込内容
   * @returns 契約ID
   */
  export function applyContract(
    itemIntroductionEstimateId: string,
    newTenantContractApplyContentList: NewTenantContractApplyContent[]
  ): Promise<string> {

    const body = {
      itemIntroductionEstimateId,
      newTenantContractApplyContentList
    }
    return new Promise((resolve, reject) => {
      axios.post(`${env("NEXT_PUBLIC_CONTRACT_APPLY_API")}`, body).then((response: CacheAxiosResponse) => {
        resolve(response.headers["x-blcloud-entity-id"])
      }).catch(error => {
        reject({ api: "applyContract", response: error.response })
      })
    })
  }

  export function getItemData(): Promise<ItemData[]> {
    return new Promise((resolve, reject) => {
      axios.get("/pr/csv/item_datas.csv").then((response: CacheAxiosResponse) => {
        const headers = [
          "id",
          "itemName",
          "section1",
          "section2",
          "condition",
          "selected",
          "point",
          "description",
          "requiredModalExplanation",
          "uncheckExplanation",
          "modalType",
          "htmlKey"
        ]
        csv({
          trim: true,
          checkType: true,
          colParser: {
            "section1": "number",
            "section2": "number",
            "selected": "number",
            "modalType": "number",
          },
          headers
        }).fromString(response.data)
          .subscribe((row, index) => {
            return new Promise((resolve) => {
              row.index = index;
              resolve();
            })
          })
          .then((csvRow: ItemData[]) => {
            resolve(csvRow)
          })
      }).catch(error => {
        reject(error)
      })
    })
  }

  export function getPriorityData(): Promise<PriorityData[]> {
    return new Promise((resolve, reject) => {
      axios.get("/pr/csv/priority_datas.csv").then((response: CacheAxiosResponse) => {
        const headers = [
          "id",
          "itemName",
          "section1",
          "sort1",
          "sort2",
          "sort3",
          "sort4",
          "sort5",
          "sort6",
          "sort7",
        ]
        csv({
          trim: true,
          checkType: true,
          colParser: {
            "section1": "number",
            "sort1": "number",
            "sort2": "number",
            "sort3": "number",
            "sort4": "number",
            "sort5": "number",
            "sort6": "number",
            "sort7": "number",
          },
          headers
        }).fromString(response.data).then((csvRow: PriorityData[]) => {
          resolve(csvRow)
        })
      }).catch(error => {
        reject(error)
      })
    })
  }

  export function getHtmlData(key: string, contentType: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axios.get(`/pr/assets/${contentType}-contents/${key}.html`).then((response: CacheAxiosResponse) => {
        resolve(response.data)
      }).catch(error => {
        reject(error)
      })
    })
  }

  export function getSettingData(): Promise<SettingData> {
    return new Promise((resolve, reject) => {
      axios.get("/pr/csv/setting.csv").then((response: CacheAxiosResponse) => {
        const headers = [
          "itemName",
          "item01",
          "item02",
          "item03",
          "item04",
          "item05",
          "item06",
        ]

        csv({
          trim: true,
          checkType: true,
          headers
        }).fromString(response.data).then((csvRow) => {
          const data = Object.fromEntries(csvRow.map(item => [item.itemName, item]))
          resolve({
            taxRate: Number(data.tax_rate.item01 || 10)
          })
        })
      }).catch(error => {
        reject(error)
      })
    })
  }

/**
   * 商品契約数量チェック
   * @param body 商品契約数量要求
   * @param agencyConsumerTenantId 代理店取引先操作時の取引先テナントID
   */
export function checkContractQuantity(
  body: ItemContractQuantityReq,
  agencyConsumerTenantId?: string
): Promise<ItemContractQuantityRes> {
  return new Promise((resolve, reject) => {
    // 既存の他のAPIに倣い、環境変数からフルパス（またはNext.jsのプロキシ用パス）を取得する形が推奨されます
    // 例: NEXT_PUBLIC_CHECK_QUANTITY_API = "https://dev-01.blcloud.jp/contract/api/v1/itemcontractquantityreq/all/check"
    const url = env("NEXT_PUBLIC_CHECK_QUANTITY_API");

    const config = {
      headers: agencyConsumerTenantId
        ? { "X-Agency-Consumer-Tenant-Id": agencyConsumerTenantId }
        : {}
    };

    axios.post(url, body, config)
      .then((response: CacheAxiosResponse) => {
        resolve(response.data);
      })
      .catch(error => {
        reject({ api: "checkContractQuantity", response: error.response });
      });
  });
}

}
